import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { css } from "@emotion/react";

import { colors } from "@dotnet/telematics-design-system";

import { useGetFilterParams } from "components/Dashboard/hooks/useGetFilterParams";
import { ProgressBarSingle } from "components/DesignComponents/ProgressBar/ProgressBarSingle";
import ParamsContext from "context/params/paramsContext";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";
import { ROUTES } from "shared/types/enums";

import ShowMore from "./ShowMore";
import { StatsContainerProps } from "./types";

const StatsContainer = ({
    data,
    filterName,
    isMoreDataLoaded,
    loadMoreData
}: StatsContainerProps) => {
    const { blue600, textDisabled } = colors;

    const { setQueryParams } = useContext(ParamsContext);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { renderFormattedNumber } = useFormattedNumber();
    const { filterParams } = useGetFilterParams();

    const [hoveredBar, setHoveredBar] = useState<string | null>(null);

    const initialStats = data.slice(0, 5);
    const stats = isMoreDataLoaded ? data : initialStats;

    const handleHover = (id: string, status: boolean) =>
        setHoveredBar(status ? id : null);

    const handleClick = (name: string) => {
        navigate(ROUTES.Devices);

        setQueryParams({
            params: { [filterName]: encodeURIComponent(name), ...filterParams },
            resetParams: true
        });
    };

    return (
        <div
            css={css({
                paddingRight: "24px",
                maxHeight: "304px",
                minHeight: "304px",
                overflowY: "auto",

                "& > div + div": {
                    marginTop: "16px"
                }
            })}
        >
            {stats.map(({ name, count }) => {
                const getHoverColor = (color: string) =>
                    hoveredBar !== null && hoveredBar !== name
                        ? color
                        : undefined;

                return (
                    <ProgressBarSingle
                        key={name}
                        id={name}
                        titleLeft={name}
                        titleRight={renderFormattedNumber(count)}
                        totalCount={stats[0].count}
                        filledCount={count}
                        filledColor={blue600}
                        titleColor={getHoverColor(textDisabled)}
                        hoverColor={getHoverColor("rgba(255, 255, 255, 0.75)")}
                        tooltipContent={t("Dashboard##take me there")}
                        customValueStyles={{
                            textDecoration:
                                hoveredBar === name ? "underline" : "none"
                        }}
                        onHoverOn={id => handleHover(id, true)}
                        onHoverOff={id => handleHover(id, false)}
                        onClick={() => handleClick(name)}
                    />
                );
            })}

            {!isMoreDataLoaded && data.length > 5 && (
                <ShowMore onClick={loadMoreData} />
            )}
        </div>
    );
};

export default StatsContainer;
